import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

export const SUPPORTED_LOCALES = ['en', 'ar'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

const RTL_LOCALES: readonly Locale[] = ['ar']

/**
 * One JSON file per section, per locale — `locales/en/portfolio.json` becomes
 * the `portfolio.*` key space.
 *
 * Discovered rather than imported by name, so adding a section is adding two
 * files and nothing else. That matters more than it looks: it is what stops
 * every feature branch from colliding in the same enormous translation file.
 */
const files = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', {
  eager: true,
  import: 'default',
})

function resources(): Record<Locale, { translation: Record<string, unknown> }> {
  const built = { en: { translation: {} }, ar: { translation: {} } } as Record<
    Locale,
    { translation: Record<string, unknown> }
  >

  for (const [path, contents] of Object.entries(files)) {
    const match = /\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path)
    if (match === null) continue

    const [, locale, section] = match
    if (!SUPPORTED_LOCALES.includes(locale as Locale)) continue

    built[locale as Locale].translation[section as string] = contents
  }

  return built
}

function storedLocale(): Locale {
  const saved = window.localStorage.getItem('awj.locale')
  return SUPPORTED_LOCALES.includes(saved as Locale) ? (saved as Locale) : 'en'
}

/** Keeps <html lang> and <html dir> honest — the whole RTL story hangs on this. */
export function applyLocale(locale: Locale): void {
  document.documentElement.lang = locale
  document.documentElement.dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr'
  window.localStorage.setItem('awj.locale', locale)
}

void i18n.use(initReactI18next).init({
  resources: resources(),
  lng: storedLocale(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
})

applyLocale(storedLocale())

export default i18n
